import {
  createExtension,
  selectedFragmentToHTML,
  type BlockNoteEditor,
  type BlockNoteEditorOptions,
} from '@blocknote/core';
import { Extension } from '@tiptap/core';
import { Plugin } from 'prosemirror-state';

import {
  extractLeadingH1,
  isEntirePageBodySelected,
  looksLikeMarkdown,
} from '@/lib/markdown/page-clipboard';

import { couplespaceSchema } from './editor-schema.web';

type CouplespaceEditor = BlockNoteEditor<
  typeof couplespaceSchema.blockSchema,
  typeof couplespaceSchema.inlineContentSchema,
  typeof couplespaceSchema.styleSchema
>;

type MarkdownClipboardRefs = {
  getPageTitle: () => string;
  onPageTitleFromPaste: (title: string) => void | Promise<void>;
};

type PasteHandler = NonNullable<
  BlockNoteEditorOptions<
    typeof couplespaceSchema.blockSchema,
    typeof couplespaceSchema.inlineContentSchema,
    typeof couplespaceSchema.styleSchema
  >['pasteHandler']
>;
type PasteHandlerContext = Parameters<NonNullable<PasteHandler>>[0];

function selectedTopLevelBlockIds(editor: CouplespaceEditor): string[] {
  const selection = editor.getSelection();
  if (!selection?.blocks.length) {
    return [];
  }
  const topLevelIds = new Set(
    (editor.document as Array<{ id?: string }>).map((block) => block.id).filter(Boolean),
  );
  return selection.blocks
    .map((block) => block.id)
    .filter((id): id is string => Boolean(id) && topLevelIds.has(id));
}

function shouldReplaceWholePage(editor: CouplespaceEditor): boolean {
  const documentBlocks = editor.document as Parameters<typeof isEntirePageBodySelected>[0];
  const selectedIds = selectedTopLevelBlockIds(editor);
  if (isEntirePageBodySelected(documentBlocks, selectedIds)) {
    return true;
  }

  const view = editor.prosemirrorView;
  if (view.state.selection.empty || selectedIds.length > 0) {
    return false;
  }

  const { from, to } = view.state.selection;
  const docSize = view.state.doc.content.size;
  return from <= 2 && to >= Math.max(0, docSize - 2);
}

function isPlainTextPaste(event: ClipboardEvent): boolean {
  const uiEvent = event as ClipboardEvent & { shiftKey?: boolean; ctrlKey?: boolean; metaKey?: boolean };
  return Boolean(uiEvent.shiftKey && (uiEvent.ctrlKey || uiEvent.metaKey));
}

export function createCouplespacePasteHandler(refs: MarkdownClipboardRefs): PasteHandler {
  return ({ event, editor, defaultPasteHandler }: PasteHandlerContext) => {
    if (isPlainTextPaste(event)) {
      const plain = event.clipboardData?.getData('text/plain') ?? '';
      if (plain) {
        editor.pasteText(plain);
        return true;
      }
      return defaultPasteHandler({
        plainTextAsMarkdown: false,
        prioritizeMarkdownOverHTML: false,
      });
    }

    const plainText = event.clipboardData?.getData('text/plain') ?? '';
    if (plainText && looksLikeMarkdown(plainText) && shouldReplaceWholePage(editor)) {
      const leading = extractLeadingH1(plainText);
      if (leading) {
        const currentTitle = refs.getPageTitle().trim().toLowerCase();
        const pastedTitle = leading.title.trim().toLowerCase();
        if (pastedTitle !== currentTitle) {
          void refs.onPageTitleFromPaste(leading.title);
        }
        editor.pasteMarkdown(leading.body || '');
        return true;
      }
    }

    return defaultPasteHandler({
      plainTextAsMarkdown: true,
      prioritizeMarkdownOverHTML: true,
    });
  };
}

export function createCouplespaceMarkdownCopyExtension(
  getEditor: () => CouplespaceEditor | null,
  refs: MarkdownClipboardRefs,
) {
  return createExtension({
    key: 'couplespace-markdown-copy',
    tiptapExtensions: [
      Extension.create({
        name: 'couplespaceMarkdownCopy',
        priority: 1000,
        addProseMirrorPlugins() {
          return [
            new Plugin({
              props: {
                handleDOMEvents: {
                  copy(view, event) {
                    const editor = getEditor();
                    if (!editor || !shouldReplaceWholePage(editor)) {
                      return false;
                    }

                    event.preventDefault();
                    event.clipboardData?.clearData();

                    const { clipboardHTML, externalHTML, markdown } = selectedFragmentToHTML(
                      view,
                      editor,
                    );
                    const title = refs.getPageTitle().trim();
                    const withTitle =
                      title && title !== 'Untitled' ? `# ${title}\n\n${markdown}` : markdown;

                    event.clipboardData?.setData('blocknote/html', clipboardHTML);
                    event.clipboardData?.setData('text/html', externalHTML);
                    event.clipboardData?.setData('text/plain', withTitle);
                    event.clipboardData?.setData('text/markdown', withTitle);
                    return true;
                  },
                  cut(view, event) {
                    const editor = getEditor();
                    if (!editor || !shouldReplaceWholePage(editor)) {
                      return false;
                    }

                    event.preventDefault();
                    event.clipboardData?.clearData();

                    const { clipboardHTML, externalHTML, markdown } = selectedFragmentToHTML(
                      view,
                      editor,
                    );
                    const title = refs.getPageTitle().trim();
                    const withTitle =
                      title && title !== 'Untitled' ? `# ${title}\n\n${markdown}` : markdown;

                    event.clipboardData?.setData('blocknote/html', clipboardHTML);
                    event.clipboardData?.setData('text/html', externalHTML);
                    event.clipboardData?.setData('text/plain', withTitle);
                    event.clipboardData?.setData('text/markdown', withTitle);

                    if (view.editable) {
                      view.dispatch(view.state.tr.deleteSelection());
                    }
                    return true;
                  },
                },
              },
            }),
          ];
        },
      }),
    ],
  });
}
